import { useCallback, useRef } from 'react';
import { UnreachableCaseError } from 'ts-essentials';
import { useMutation } from '@apollo/client';
import {
  DashboardColumnType,
  IDashboardColumnGridUpdateInput,
  IDashboardGridUpdateInput,
  IDashboardGridUpdateMutation,
  IDashboardGridUpdateMutationVariables,
  IDashboardInstanceFieldsFragment,
  IDashboardRowGridUpdateInput,
} from '../../../../utils/apollo/graphqlSchema';
import { DASHBOARD_GRID_UPDATE } from '../../../../utils/apollo/dashboards';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import { dashboardUpdated } from '../../../../utils/analytics/events/dashboard/events';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import useUpdateDashboardStore from '../useUpdateDashboardStore';
import produce from 'immer';
import useDashboardReload from '../useDashboardReload';
import { IRowsProducer } from './gridUpdaters';

const prepareDashboardGridUpdateInput = (
  dashboardInstance: Pick<
    IDashboardInstanceFieldsFragment,
    'id' | 'baseId' | 'rows'
  >,
): IDashboardGridUpdateInput => {
  const { baseId, id: instanceId, rows: dashboardRows } = dashboardInstance;
  const rows = dashboardRows.map<IDashboardRowGridUpdateInput>(
    ({ columns: rowColumns, minHeight }) => {
      const columns = rowColumns.map<IDashboardColumnGridUpdateInput>(
        (column) => {
          if (column.__typename === 'DashboardAnalysisColumn') {
            const {
              analysis: { id },
              colSpan,
            } = column;

            return {
              type: DashboardColumnType.Analysis,
              colSpan,
              analysis: {
                analysisId: id,
              },
              richText: null,
              externalEmbed: null,
              imageEmbed: null,
            } satisfies IDashboardColumnGridUpdateInput;
          } else if (column.__typename === 'DashboardRichTextColumn') {
            const {
              richText: { id: richTextId },
              colSpan,
            } = column;

            return {
              type: DashboardColumnType.RichText,
              colSpan,
              richText: { richTextId },
              analysis: null,
              externalEmbed: null,
              imageEmbed: null,
            } satisfies IDashboardColumnGridUpdateInput;
          } else if (column.__typename === 'DashboardExternalEmbedColumn') {
            const {
              externalEmbed: { id: externalEmbedId },
              colSpan,
            } = column;

            return {
              type: DashboardColumnType.ExternalEmbed,
              colSpan,
              analysis: null,
              richText: null,
              externalEmbed: { externalEmbedId },
              imageEmbed: null,
            } satisfies IDashboardColumnGridUpdateInput;
          } else if (column.__typename === 'DashboardImageEmbedColumn') {
            const {
              imageEmbed: { id: imageEmbedId },
              colSpan,
            } = column;

            return {
              type: DashboardColumnType.ImageEmbed,
              colSpan,
              analysis: null,
              richText: null,
              externalEmbed: null,
              imageEmbed: { imageEmbedId },
            } satisfies IDashboardColumnGridUpdateInput;
          } else {
            throw new UnreachableCaseError(column);
          }
        },
      );

      return {
        columns,
        minHeight,
      };
    },
  );

  return {
    baseId,
    instanceId,
    rows,
  };
};

const getDashboardWithUpdatedRows = (
  dashboard: IDashboardInstanceFieldsFragment,
  newRows: IDashboardInstanceFieldsFragment['rows'],
) =>
  produce(dashboard, (draft) => {
    draft.rows = newRows;
  });

/**
 * Updates the current dashboard grid (rows/cols) and provides a way to save the changes to the server, hence the 2
 * functions it returns:
 * (a) updateDashboardGrid - only updates the in-memory state of the dashboard
 * (b) saveDashboardGrid - submits the updated grid to Kobol and updates in-memory state
 *
 * Edit operations (e.g. changing Rich Text) does not require update of the grid as it doesn't change the rows or
 * columns as Kobol provides a way to update just that content, w/o submitting the entire grid
 * Add / Delete / Move operations change the grid layout thus it requires update of the grid on the server
 *
 */
const useDashboardUpdateGrid = (
  dashboard: IDashboardInstanceFieldsFragment,
) => {
  const { track } = useAnalytics();
  const updateDashboardStore = useUpdateDashboardStore();
  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const isEditDashboardRunning = useRef(false);

  const [dashboardGridUpdateMutation] = useMutation<
    IDashboardGridUpdateMutation,
    IDashboardGridUpdateMutationVariables
  >(DASHBOARD_GRID_UPDATE);

  const saveDashboardGrid = useCallback(
    async (rowsProducer: IRowsProducer) => {
      try {
        // avoid concurrent updates. For example, change a rich text and
        // start resizing. These updates happen quckly and Kobol returns an
        // error if they overlap. Using this flag drops one of the updates.
        // A better solution is to create a debounced `handleEditDashboard`
        // function, but we need to accumulate client-side the changes
        // (as we did prior removal of Edit Layout batching) and submit them at
        // once.
        if (isEditDashboardRunning.current) {
          console.warn(
            'Concurrent update of the dashboard. Dropping update :(',
          );
          return;
        }
        isEditDashboardRunning.current = true;

        // This is quick hack to disable any user interactions when the update
        // is running. The following undesired effect happens:
        // 1. You update a rich text area in Dashboard A
        // 2. Click on another dashboard B. This triggers saving of dashboard
        // 3. Race-condition: the new dashboard loads and the result of the save
        // operation loads.
        // The result is: you see Dashboard B selected in the
        // navigation with Dashboard A contents.
        // TODO: we need better solution
        document.documentElement.style.pointerEvents = 'none';

        const newRows = rowsProducer(dashboard.rows);
        const response = await dashboardGridUpdateMutation({
          variables: {
            input: prepareDashboardGridUpdateInput({
              baseId: dashboard.baseId,
              id: dashboard.id,
              rows: newRows,
            }),
          },
        });

        const result = response.data?.dashboardGridUpdate;
        assertNotNil(result);

        const { entity, errors } = result;
        if (printGraphqlErrors(errors)) {
          await reloadDashboard();
          return;
        }
        assertNotNil(entity);

        // NB! we are preserving the state rows (newRows) and not using the rows from the response (entity.rows)
        // because every time we update the grid, the grid rows change their ID. This ID is used as component (React)
        // key and causes remounting of the child components (reports, embeds, images) visually looks like reload.
        // Kobol ensured that if it returns success (i.e. no errors) these are the exact rows at the server so this
        // operation should be safe(-ish)
        const newDashboard = getDashboardWithUpdatedRows(entity, newRows);
        track(dashboardUpdated(newDashboard));
        updateDashboardStore({ dashboard: newDashboard, retainAnalyses: true });
      } finally {
        document.documentElement.style.pointerEvents = '';
        isEditDashboardRunning.current = false;
      }
    },
    [
      dashboard.baseId,
      dashboard.id,
      dashboard.rows,
      dashboardGridUpdateMutation,
      reloadDashboard,
      track,
      updateDashboardStore,
    ],
  );

  const updateDashboardGrid = useCallback(
    (rowsProducer: IRowsProducer) => {
      const newDashboard = getDashboardWithUpdatedRows(
        dashboard,
        rowsProducer(dashboard.rows),
      );

      track(dashboardUpdated(newDashboard));
      updateDashboardStore({ dashboard: newDashboard, retainAnalyses: true });
    },
    [dashboard, track, updateDashboardStore],
  );

  return {
    saveDashboardGrid,
    updateDashboardGrid,
  };
};

export default useDashboardUpdateGrid;
